import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from 'moment';

//Components
import { Button, Tooltip, Toolbar } from "@gpstrackit/react-controls";
import { NotificationManager } from "Components/Notifications";
import InputDate from 'Components/InputDate';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Icon, Grid } from '@mui/material';
import Filters from 'Components/Filters';

//Views
import DeleteClipRequest from 'Modules/devices/.components/deleteClipRequest';

//Helpers
import { getMinDateLimitBehaviour, getDateFormatted } from 'Core/data/Helpers';
import { HasPermission } from "Core/security";
import { selectEntityState } from 'Core/common/redux/infiniteScrollSlice';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

//Actions
import {
    clipRequestGetUserRequest,
    searchEntityRequest,
    vidfleetInformationOpenModal,
    deleteClipRequestOpenModal,
    vidFleetVideoOpenModal
} from 'Redux/actions'

//Styles
import './index.scss'

const RANGE_MODE_ROWS = 50;

const ClipRequest = (props) => {
    const { height, device } = props;

    const { messages } = useIntl();
    const tableRef = useRef();
    const toolbarRef = useRef();
    const dispatch = useDispatch();

    //Variables

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());

    //Redux States
    const deleteClipRequestOpenModalV = useSelector(state => state.deleteClipRequestRedux.openModal);
    const result = useSelector(state => state.createClipRequestRedux.result);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const user = useSelector(state => state.securityRedux.user);
    const lang = useSelector(state => state.commonRedux.language);
    const { users } = useSelector(state => state.clipRequestTabRedux);
    const entityState = useSelector(selectEntityState('clip-request'));

    //Local States
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: startDate,
        end: endDate
    })
    const [openSearch, setOpenSearch] = useState("");
    const [columns, setColumns] = useState([
        {
            key: "view",
            locked: true,
            show: true,
            title: "",
            width: 50,
            icon: true,
            render: (item) => {
                return (
                    <div>
                        <Tooltip title={messages['system_alert_logs_tab_view_details']}>
                            <Icon style={{ cursor: "pointer" }} onClick={() => {
                                showMedia(item)
                            }} >
                                remove_red_eye
                            </Icon>
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: "thumbnailUrl",
            field: "thumbnailUrl",
            title: messages['clip_request_url'],
            show: true,
            resize: true,
            width: 300,
            export: true,
            functionString: 'return row?.thumbnailUrl',
            code: "clip_request_url",
            render: (item) => {
                let thumbnailUrl = item?.thumbnailUrl || ''
                if (thumbnailUrl?.length >= 7) {
                    return (
                        <Tooltip title={item?.thumbnailUrl}>
                            <span>{thumbnailUrl.substring(0, 35) + "..."}</span>
                        </Tooltip>
                    )
                }
                return <span>{thumbnailUrl}</span>
            }
        },
        {
            key: "transactionId",
            field: "transactionId",
            title: messages['clip_request_transaction_id'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.transactionId',
            code: "clip_request_transaction_id",
            render: (item) => {
                return <span>{item?.transactionId}</span>
            }
        },
        {
            key: "timeFrame",
            title: messages['clip_request_time_frame'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
                let timezone = '${device?.timezone}'

                let formatDateStart = moment.unix(row?.startDate / 1000);
                let formatDateEnd = moment.unix(row?.endDate / 1000);

                if(timezone){
                    formatDateStart = formatDateStart.tz(timezone).format('h:mm  A');
                    formatDateEnd = formatDateEnd.tz(timezone).format('h:mm A, z');
                }else {
                    formatDateStart = formatDateStart.format('h:mm  A');
                    formatDateEnd = formatDateEnd.format('h:mm A');
                }

                return formatDateStart +" - "+ formatDateEnd
            `,
            code: "clip_request_time_frame",
            render: (item) => {
                let formatDateStart = moment.unix(item?.startDate / 1000);
                let formatDateEnd = moment.unix(item?.endDate / 1000);
                if (device?.timezone) {
                    formatDateStart = formatDateStart.tz(device?.timezone)
                    formatDateEnd = formatDateEnd.tz(device?.timezone)
                }
                formatDateStart = moment(formatDateStart).format("h:mm  A");
                formatDateEnd = moment(formatDateEnd).format("h:mm A, " + (device?.timezone ? "z" : ""));
                const finalFormat = `${formatDateStart} - ${formatDateEnd}`;
                return <span>{finalFormat}</span>
            }
        },
        {
            key: "startDate",
            title: messages['clip_request_clip_date'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: `
                let timezone = '${device?.timezone}'

                let formatDateStart = moment.unix(row?.startDate / 1000);

                if(timezone){
                    formatDateStart = formatDateStart.tz(timezone).format('MMM DD YYYY z');
                } else {
                    formatDateStart = formatDateStart.format('MMM DD YYYY');
                }

                return formatDateStart
            `,
            code: "clip_request_clip_date",
            render: (item) => {
                return <span>{getDateFormatted(item.startDate, "long", "date", null, device?.timezone)}</span>
            }
        },
        {
            key: "uploadType",
            field: "uploadType",
            title: messages['clip_request_upload_type'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.uploadType',
            code: "clip_request_upload_type",
            render: (item) => {
                return <span>{item?.uploadType}</span>
            }
        },
        {
            key: "status",
            field: "status",
            title: messages['clip_request_status'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.status',
            code: "clip_request_status",
            render: (item) => {
                return <span>{item?.status}</span>
            }
        },
        {
            key: "driver_name",
            title: messages['clip_request_driver'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.driver?.name',
            code: "clip_request_driver",
            render: (item) => {
                return <span>{item?.driver?.name}</span>
            }
        },
        {
            key: "requestor",
            title: messages['clip_request_requestor'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
                if(!row.createdBy || row.createdBy === "External"){
                    return ''
                }

                let pmUsers = ${JSON.stringify(pmUsers)}
                let users = ${JSON.stringify(users)}
                let account = '${device?.company?.username}'

                if(row?.createdBy.includes("PM_")){
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.username || ''
                }

                let user = row?.createdBy.split("_");

                if(user && user.length === 2){
                    return account
                } else if(user.length > 2){
                    let current = users.find((item) => item.id == user[2])
                    return current?.userName
                }

                return ''
            `,
            code: "clip_request_requestor",
            render: (item) => {
                return <span>{item?.createdByUser?.username}</span>
            }
        },
        {
            key: "errorMsg",
            field: "errorMsg",
            title: messages['clip_request_error_msg'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.errorMsg',
            code: "clip_request_error_msg",
            render: (item) => {
                return <span>{item?.errorMsg}</span>
            }
        }
    ]);
    const [filters, setFilters] = useState({
        startDate: tempStartDate,
        endDate: endDate,
        openSearch: null,
    })

    const [last, setLast] = useState({ id: '20', name: '20' });
    const [type, setType] = useState('last');

    //Functions
    const onChange = (value) => {
        const { start, end } = value;
        let endDate = end ? end.setHours(23, 59, 59) : null
        onChangeStartEndDate({
            start,
            end: endDate
        })
    };

    const onChangeOpenSearch = (value) => {
        let fltrs = filters;
        fltrs.openSearch = value
        setFilters(fltrs)
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;

        setFilters({ ...fltrs });
    }

    const showMedia = (source) => {
        let data = {
            messages: messages, //For to access to language keys
            mode: "picture",
            thumbnailUrl: source?.url,
            id: source?.unit?.sn, //This could be a ESN or Clip Id
            autoplay: false, //If is true the video will be played automatically
            disabled: false
        }
        if (source?.url?.includes(".mp4")) {
            data.mode = "video";
            data.type = "playback";
            data.id = source.clipID;
        }
        dispatch(vidFleetVideoOpenModal(data, true, source?.id))
    }

    const applyFilters = (paramType) => {
        const currentType = paramType ?? type;


        if (currentType !== "last" && (!filters.startDate || !filters.endDate)) {
            NotificationManager.warning(
                messages['raw_data_tab_date_range'],
                messages['raw_data_tab_raw_data_modal_title'],
                5000,
            );

            return
        }

        let currentNewConditions = [];
        let startDate = moment.utc(filters.startDate);
        let endDate = moment.utc(filters.endDate);

        startDate = startDate.unix() * 1000
        endDate = endDate.unix() * 1000

        if (currentType !== "last")
            currentNewConditions && currentNewConditions.push({
                fields: ["unitId", "deviceId"],
                terms: [device?.id],
                exact: true,
                range: {
                    fields: ["startDate"],
                    GTENumber: startDate,
                    LTENumber: endDate
                },
                conditionOperator: "AND"
            });
        else
            currentNewConditions && currentNewConditions.push({
                fields: ["unitId", "deviceId"],
                terms: [device?.id],
                exact: true,
                conditionOperator: "AND"
            });

        if (filters.openSearch?.length) {
            currentNewConditions.push({
                fields: ["transactionId", "status", "driver_name", "uploadType"],
                terms: filters.openSearch,
                exact: false
            })
        }
        
        // Use options object API for getData
        const effectiveRows = currentType === 'last' ? parseInt(last.id, 10) : RANGE_MODE_ROWS;
        const effectiveSort = currentType === 'last' ? [{ field: "startDate", order: "DESC" }] : tableRef.current.sort;
        tableRef?.current?.getData({ 
            newRows: effectiveRows, 
            newSort: effectiveSort, 
            newConditions: currentNewConditions 
        });

    }

    /* useEffect(() => {
        if (openSearch || openSearch === "") {
            const timer = setTimeout(() => {
                let currentNewConditions = []

                let startDate = moment.utc(filters.startDate);
                let endDate = moment.utc(filters.endDate);

                startDate = startDate.unix() * 1000
                endDate = endDate.unix() * 1000

                if (type !== 'last')
                    currentNewConditions && currentNewConditions.push({
                        fields: ["unitId", "deviceId"],
                        terms: [device?.id],
                        exact: true,
                        range: {
                            fields: ["startDate"],
                            GTENumber: startDate,
                            LTENumber: endDate
                        }
                    });
                else
                    currentNewConditions && currentNewConditions.push({
                        fields: ["unitId", "deviceId"],
                        terms: [device?.id],
                        exact: true
                    });

                if (filters.openSearch?.length) {
                    currentNewConditions.push({
                        fields: ["transactionId", "status", "driver_name", "uploadType"],
                        terms: filters.openSearch,
                        exact: false
                    })
                }
                //execute the account search
                
                tableRef?.current?.getData(null, type === 'last' ? last.id : null, null, currentNewConditions);
            }, 500)
            return () => clearTimeout(timer);
        }
    }, [openSearch]) */

    useEffect(() => {
        if (result && result.id) {
            //tableRef?.current?.getData()
        }
    }, [result])

    useEffect(() => {
        dispatch(searchEntityRequest({ key: "pmUsers" }));
        if (device?.companyId) {
            dispatch(clipRequestGetUserRequest(device?.companyId))
        }
    }, [])

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "requestor" ? `
                    if(!row.createdBy || row.createdBy === "External"){
                        return ''
                    }

                    let pmUsers = ${JSON.stringify(pmUsers)}
                    let users = ${JSON.stringify(users)}
                    let account = '${device?.company?.username}'

                    if(row?.createdBy.includes("PM_")){
                        pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                        return pmUsers.username || ''
                    }

                    let user = row?.createdBy.split("_");

                    if(user && user.length === 2){
                        return account
                    } else if(user.length > 2){
                        let current = users.find((item) => item.id == user[2])
                        return current?.userName
                    }

                    return ''
                `: item.key === "startDate" ? `
                    let timezone = '${device?.timezone}'

                    let formatDateStart = moment.unix(row?.startDate / 1000);

                    if(timezone){
                        formatDateStart = formatDateStart.tz(timezone).format('MMM DD YYYY z');
                    }else {
                        formatDateStart = formatDateStart.format('MMM DD YYYY');
                    }

                    return formatDateStart
                    `: item.functionString
            }))

            setColumns([...currentColumns])
        }
    }, [pmUsers, users, lang, device])

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    // Fields to search when user types free-form text (tag mode)
    const searchFields = ["transactionId", "uploadType", "status"];

    /**
     * Handle condition changes from search/filters/preferences
     * 
     * DataView calls this in two modes:
     * 1. Tags-only: onChangeConditions(tags, null, ...) - from SearchInput selections/typing
     * 2. Full conditions: onChangeConditions(null, conditions, ...) - from preferences restore
     * 
     * We must handle BOTH cases to properly send conditions to GraphQL.
     */
    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        let currentConditions = [];
        let currentTags = [];
        let currentType = type;
        let currentLast = last;

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 1: Handle tags-only vs full-conditions invocation
        // ═══════════════════════════════════════════════════════════════════════
        if (tags) {
            // Tags-only mode: DataView called with search tags from SearchInput
            // Get existing conditions from tableRef and use the provided tags
            currentConditions = tableRef?.current?.conditions || [];
            currentTags = tags;
        }
        if (newConditions) {
            // Full conditions mode: DataView called with conditions (from preferences)
            currentConditions = newConditions || [];
            // Extract tags from conditions if present
            currentTags = currentConditions?.filter((x) => x?.tags)?.[0]?.tags || [];
            if (currentTags && currentTags.length > 0) {
                tableRef?.current?.setTags(currentTags);
            }
        }

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 2: Process date range from conditions (preferences restore)
        // ═══════════════════════════════════════════════════════════════════════
        let startDateMs = moment.utc(filters.startDate).unix() * 1000;
        let endDateMs = moment.utc(filters.endDate).unix() * 1000;
        let fltrs = filters;

        let currentDateType = currentConditions.find((item) => item?.range?.fields);
        if (currentDateType) {
            fltrs.startDate = new Date(currentDateType?.range?.GTENumber);
            fltrs.endDate = new Date(currentDateType?.range?.LTENumber);
            startDateMs = currentDateType?.range?.GTENumber;
            endDateMs = currentDateType?.range?.LTENumber;
            setFilters({ ...fltrs });
            setType('range');
            currentType = 'range';
            
            // Sync date picker state with loaded preferences
            setStartDate(fltrs.startDate);
            setEndDate(fltrs.endDate);
            setBeforeRangeDate({
                start: fltrs.startDate,
                end: fltrs.endDate
            });
            
           //if (rows) setLast({ id: 5, name: 5 });
        }

        if (rows) {
            setLast({ id: `${rows}`, name: `${rows}` });
            currentLast = { id: `${rows}`, name: `${rows}` };
        }

        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 3: Filter out base conditions we'll rebuild (device, search fields)
        // ═══════════════════════════════════════════════════════════════════════
        currentConditions = currentConditions?.filter((item) => 
            item?.fields && 
            item?.fields[0] !== 'id' && 
            item?.fields[0] !== 'unitId' && 
            item?.fields[0] !== 'deviceId' &&
            !searchFields.includes(item?.fields[0])
        ) || [];

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 4: Process search tags → GraphQL conditions
        // ═══════════════════════════════════════════════════════════════════════
        
        // Dropdown selections have extra fields from GraphQL response (transactionId, uploadType, etc.)
        // Typed tags only have id/label/name from user input
        const isDropdownSelection = (item) => 
            item.transactionId !== undefined || 
            item.uploadType !== undefined || 
            item.createdByUser !== undefined;

        // Dropdown selections → exact match on ID field
        // Numeric IDs (selected from dropdown) → exact match on ID field only
        let tagsById = currentTags.filter((item) => !isNaN(parseInt(item.id)));
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => parseInt(item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        // String IDs (typed by user) → fuzzy search across multiple fields
        let tagsByLabel = currentTags.filter((item) => isNaN(parseInt(item.id)));
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.id))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 5: Add device filter with optional date range
        // ═══════════════════════════════════════════════════════════════════════
        let effectiveSort = sort;

        if (currentType !== 'last') {
            currentConditions.push({
                fields: ["unitId", "deviceId"],
                terms: [device?.id],
                exact: true,
                range: {
                    fields: ["startDate"],
                    GTENumber: startDateMs,
                    LTENumber: endDateMs
                },
                conditionOperator: "AND"
            });
        } else {
            currentConditions.push({
                fields: ["unitId", "deviceId"],
                terms: [device?.id],
                exact: true,
                conditionOperator: "AND"
            });
            effectiveSort = [{ field: "startDate", order: "DESC" }];
        }

        // ═══════════════════════════════════════════════════════════════════════
        // STEP 6: Determine rows and trigger data fetch
        // ═══════════════════════════════════════════════════════════════════════
        const effectiveRows = currentType === 'last' 
            ? parseInt(currentLast?.id || last?.id, 10) 
            : RANGE_MODE_ROWS;

        tableRef?.current?.getData({ 
            newRows: effectiveRows, 
            newSort: effectiveSort, 
            newConditions: currentConditions, 
            newColumns 
        });
    };

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = (items) => setColumns(items);

    /**
     * Extra button component - renders custom filter UI and new clip button
     */
    const renderExtraButton = () => {
        return (
            <div className="clip-request-filters" style={{ /* width: "100%",  */display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {/* <div className="clip-request-toolbar">
                    <Toolbar
                        onSearch={(value) => {
                            setOpenSearch(value);
                            onChangeOpenSearch(value)
                        }}
                        ref={toolbarRef}
                    />
                </div> */}

                 <Filters
                     startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        setBeforeRangeDate={setBeforeRangeDate}
                        beforeRangeDate={beforeRangeDate}
                        onChangeFilters={() => applyFilters()}
                        onChange={onChange}
                        onChangeStartEndDate={onChangeStartEndDate}
                        last={last}
                        setLast={setLast}
                        type={type}
                        setType={(value) => {
                            setType(value)
                            if (value === 'range') {
                                tableRef?.current?.setRows(RANGE_MODE_ROWS);
                                //setLast({ id: '10', name: '10' })
                            }
                            applyFilters(value)
                     }}
                     extraButtonFilter={() => (
                        <div>
                            <Button
                                disabled={!HasPermission(user, "clpr", "c")}
                                className="btn btn-dark-blue btn-block"
                                size="sm"
                                style={{ height: 42 }}
                                onClick={() => {
                                    dispatch(vidfleetInformationOpenModal(device?.esn, device?.id, device?.timezone, device?.companyId, true))
                                }}
                            >
                                {messages['clip_request_new_clip']}
                            </Button>
                        </div>

                     )}
                 />
            </div>
        )
    };

    /**
     * Context menu options for row actions
     */
    const menuOptions = [
        {
            id: "delete-clip",
            label: messages['delete'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "clpr", "d"),
            action: (items) => {
                dispatch(deleteClipRequestOpenModal(items[0]))
            }
        }
    ];

    const escapeCsvValue = (val) => {
        const str = val === undefined || val === null ? '' : String(val);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const evaluateFunctionString = (functionString, row) => {
        if (!functionString) return '';
        try {
            return new Function('row', 'moment', functionString)(row, moment);
        } catch (error) {
            return '';
        }
    };

    const buildDynamicFields = (columnsToExport, baseQuery, includeHidden = false) => {
        let params = baseQuery;
        const columnsToUse = includeHidden
            ? columnsToExport.filter((item) => item.export)
            : columnsToExport.filter((item) => item.show);

        columnsToUse.map((column) => {
            params += " "
            let fields = column.field && column.field.split(",") || []
            fields.map(f => {
                let array = f.split(".")
                if (array.length > 1) {
                    let text = "";
                    let closeText = "";
                    if (params.includes(array[0])) {
                        let divider = params.split(`${array[0]} { `)
                        params = divider[0] + ` ${array[0]} { ` + `${array[1]} ` + divider[1]
                    } else {
                        for (let i = 0; i < array.length; i++) {
                            if (!params.includes(array[0])) {
                                text += array[i]
                            }
                            if (i < array.length - 1) {
                                text += " { "
                                closeText += " } "
                            }
                        }
                    }
                    params += text + closeText
                }
                else params += array[0]
            })
        })
        return params;
    };

    const buildGraphQLQuery = ({ functionName, conditionsType, sortType, fields, extraFields = '' }) => {
        const sortParam = sortType ? `, $sort: [${sortType}]` : '';
        const sortArg = sortType ? ', sort: $sort' : '';
        return `
            query ${functionName} ($conditions: [${conditionsType}]${sortParam}, $limit: Int, $offset: Int) {
                result: ${functionName} (conditions: $conditions${sortArg}, limit: $limit, offset: $offset) {
                    items {
                        ${fields}
                    }
                    ${extraFields}
                }
            }
        `;
    };

    const generateCsvFromRows = (rows, columnsVisibility) => {
        const exportColumns = (columns || []).filter((col) =>
            columnsVisibility === 'All' ? col.export : col.export && col.show
        );

        const headerRow = exportColumns.map((col) => col.title || col.field);
        const csvLines = [headerRow.map(escapeCsvValue).join(',')];

        rows.forEach((row) => {
            const line = exportColumns.map((col) =>
                escapeCsvValue(evaluateFunctionString(col.functionString, row))
            );
            csvLines.push(line.join(','));
        });

        const csvContent = csvLines.join('\n');
        const date = new Date();
        const fileName = `cliprequest_${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}.csv`;

        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;encoding:utf-8' }));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        NotificationManager.success(`Exported ${rows.length} rows`, 'Export Complete', 3000);
    };

    const exportLocalCsv = async (currentType, columnsVisibility) => {
        NotificationManager.info('Generating export...', 'Export Started', 2000);
        let rowsToExport = [];

        if (columnsVisibility === 'All') {
            try {
                const baseQuery = "id status unit { id label } clipInformation createdOn updatedOn";
                const dynamicFields = buildDynamicFields(columns, baseQuery, true);

                const query = buildGraphQLQuery({
                    functionName: 'searchClipRequests',
                    conditionsType: 'ClipRequestSearchInput',
                    sortType: 'ClipRequestSortInput',
                    fields: dynamicFields,
                    extraFields: 'total',
                });

                const sanitizedConditions = (tableRef?.current?.conditions || [])
                    ?.map(({ tags, account, ...condition }) => condition) || [];

                const variables = {
                    limit: entityState?.items?.length || 500,
                    offset: 0,
                    sort: [{ field: "createdOn", order: "DESC" }],
                    conditions: sanitizedConditions,
                };

                const response = await clientQuery(query, variables, endpoints.GRAPHQL_GENERAL);
                rowsToExport = response?.result?.items || [];
            } catch (error) {
                console.error('Error fetching complete data for export:', error);
                NotificationManager.error('Error exporting data', 'Export Failed', 5000);
                return;
            }
        } else {
            rowsToExport = entityState?.items || [];
        }

        if (rowsToExport.length === 0) {
            NotificationManager.warning('No data to export', 'Export Warning', 5000);
            return;
        }

        generateCsvFromRows(rowsToExport, columnsVisibility);
    };

    return (
        <div className="pm-clip-request" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name={`pm-clip-request`}
                entity="clip-request"
                showTotalCount={type !== 'last'}
                infiniteScroll={type !== 'last'}
                graphqlConfig={{
                    function: "searchClipRequests",
                    sortType: "ClipRequestSortInput",
                    conditionsType: "ClipRequestSearchInput",
                    customQuery: "id thumbnailUrl url clipID startDate endDate driver { id name } createdBy createdByUser { username } transactionId uploadType",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    //disabled: true,
                    conditions: ["transactionId", "uploadType", "status"],
                    optionValue: "id",
                    optionLabel: "transactionId",
                    extraField: "transactionId uploadType status createdByUser { username }",
                    fields: ["transactionId", "uploadType", "status"],
                    custom: { fields: ["unitId", "deviceId"], terms: [device?.id], exact: true },
                    openCustomOptions: (option) => {
                        if (!option) return "";
                        if (!option.uploadType) {
                            return option.label || option.id;
                        }
                        const tx = option.transactionId || option.id || "";
                        const user = option.createdByUser?.username || option.createdBy || "";
                        const upload = option.uploadType || "";
                        const status = option.status || "";
                        const main = tx + (user ? ` - ${user}` : "");
                        return upload ? `#${main} (${upload}${status ? ` - ${status}` : ""})` : `#${main}`;
                    },
                }}
                exportConfig={{
                    fileName: "cliprequest",
                    generate: type === 'last' ? exportLocalCsv : undefined,
                }}
                uiConfig={{
                    hideSplit: true,
                    hideFilters: true,
                    hidePagination: type === "last",
                    height: height || 500,
                    optionsButtonColSum: 1
                }}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                extraButton={renderExtraButton}
                options={menuOptions}
                menuOptions={(row) => {
                    let actions = [
                        messages['delete']
                    ]
                    return actions
                }}
                onClickMenu={(event, item) => {
                    if (event === messages['delete']) {
                        if (HasPermission(user, "clpr", "d")) {
                            dispatch(deleteClipRequestOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['delete_clip_request_title'],
                                5000,
                            );
                        }
                    }
                }}
            />
            {deleteClipRequestOpenModalV && <DeleteClipRequest tableRef={tableRef} />}
        </div>
    )
}
export default ClipRequest;